import { useState, useEffect } from 'react';
import { Runner, RaceMeeting } from '../types';
import { fetchTodaysRaceData } from '../services/dataService';

interface UseRaceDataReturn {
  meetings: RaceMeeting[];
  runners: Runner[];
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  refetch: () => Promise<void>;
}

const friendlyError = (err: unknown): string => {
  const msg = err instanceof Error ? err.message : String(err);
  if (/network|fetch|failed to fetch/i.test(msg)) {
    return "We couldn't reach the racing data service. Check your connection and try again.";
  }
  if (/jwt|401|auth/i.test(msg)) {
    return 'Your session expired. Please sign in again.';
  }
  return "Something went wrong loading today's races. Please try again — if it persists, let us know.";
};

export const useRaceData = (): UseRaceDataReturn => {
  const [meetings, setMeetings] = useState<RaceMeeting[]>([]);
  const [runners, setRunners] = useState<Runner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsEmpty(false);

      const { meetings: meetingsData, runners: runnersData } = await fetchTodaysRaceData();

      setMeetings(meetingsData);
      setRunners(runnersData);
      setIsEmpty(meetingsData.length === 0);
    } catch (err) {
      console.error('Error fetching race data:', err);
      setError(friendlyError(err));
      setMeetings([]);
      setRunners([]);
      setIsEmpty(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    meetings,
    runners,
    loading,
    error,
    isEmpty,
    refetch: fetchData
  };
};
